import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;

const logoUrl = 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/b98f8b06412a69b29f2607cb4b17b272.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Projects', path: '/projects', hasDropdown: true },
  { label: 'Services', path: '/#services' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const dropdownProjects = [
  { title: 'Margala Garden Housing Society', path: '/projects/margala-garden-housing', desc: 'Islamabad E-19' },
  { title: 'Blue World City', path: '/projects/blue-world-city', desc: 'Chakri Road' },
  { title: 'Park View City', path: '/projects/park-view-city', desc: 'Malir Cantt, Karachi' },
  { title: 'DHA Valley', path: '/projects/dha-valley', desc: 'Expressway' },
  { title: 'Capital Smart City', path: '/projects/capital-smart-city', desc: 'M-2 Motorway' },
  { title: 'Nova City', path: '/projects/nova-city', desc: 'Fateh Jang Road' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = useCallback(
    (path: string) => {
      setMobileOpen(false);
      setDropdownOpen(false);
      if (path.startsWith('/#')) {
        const id = path.replace('/#', '');
        if (location.pathname !== '/') {
          navigate('/');
        }
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      } else {
        navigate(path);
      }
    },
    [navigate, location.pathname]
  );

  const handleDropdownEnter = () => {
    if (dropdownTimeout) clearTimeout(dropdownTimeout);
    setDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    const t = setTimeout(() => setDropdownOpen(false), 180);
    setDropdownTimeout(t);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#1a1a1a]/95 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.25)]'
            : 'bg-[#1a1a1a]'
        }`}
      >
        <div className="w-full px-5 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[68px] md:h-[76px]">
            {/* Left: Logo + Brand */}
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('/');
              }}
              className="flex items-center gap-3 shrink-0 group"
            >
              <img
                src={logoUrl}
                alt="Sunshine Real Estate"
                className="h-9 w-9 md:h-11 md:w-11 object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="hidden sm:block">
                <div className="text-[13px] md:text-[15px] font-bold leading-tight text-white tracking-tight">
                  Sunshine Real Estate
                </div>
                <div className="text-[9px] md:text-[11px] text-white/50 font-medium tracking-wide uppercase">
                  Marketing SMC (Pvt.) Ltd.
                </div>
              </div>
            </a>

            {/* Center: Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={link.hasDropdown ? handleDropdownEnter : undefined}
                  onMouseLeave={link.hasDropdown ? handleDropdownLeave : undefined}
                >
                  <a
                    href={link.path}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.path);
                    }}
                    className={`relative px-4 py-2 text-[13px] font-medium tracking-wide rounded-lg transition-all duration-300 whitespace-nowrap flex items-center gap-1 text-white/80 hover:text-white ${
                      location.pathname === link.path || (link.path === '/projects' && location.pathname.startsWith('/projects'))
                        ? 'text-white'
                        : ''
                    }`}
                  >
                    {location.pathname === link.path || (link.path === '/projects' && location.pathname.startsWith('/projects')) && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#d4853a]" />
                    )}
                    {link.label}
                    {link.hasDropdown && (
                      <i
                        className="ri-arrow-down-s-line text-xs text-white/50 transition-all duration-300"
                        style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    )}
                  </a>

                  {/* Projects Dropdown */}
                  {link.hasDropdown && (
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[340px] bg-[#222] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.5)] border border-white/[0.06] overflow-hidden transition-all duration-300 origin-top ${
                        dropdownOpen
                          ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                          : 'opacity-0 -translate-y-2 scale-[0.97] pointer-events-none'
                      }`}
                    >
                      <div className="p-2">
                        <div className="text-[10px] font-bold text-white/30 uppercase tracking-[0.15em] px-3 py-2">
                          Featured Projects
                        </div>
                        {dropdownProjects.map((proj) => (
                          <a
                            key={proj.path}
                            href={proj.path}
                            onClick={(e) => {
                              e.preventDefault();
                              handleNavClick(proj.path);
                            }}
                            className="flex flex-col px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors duration-200 group"
                          >
                            <span className="text-[13px] font-semibold text-white/90 group-hover:text-[#d4853a] transition-colors duration-200">
                              {proj.title}
                            </span>
                            <span className="text-[11px] text-white/40">{proj.desc}</span>
                          </a>
                        ))}
                        <a
                          href="/projects"
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavClick('/projects');
                          }}
                          className="flex items-center justify-between px-3 py-2.5 mt-1 rounded-lg hover:bg-[#d4853a]/10 text-[#d4853a] text-[13px] font-semibold transition-colors duration-200"
                        >
                          View All Projects
                          <i className="ri-arrow-right-line" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right: Phone + WhatsApp CTA */}
            <div className="flex items-center gap-4">
              {/* Phone — desktop only, subtle */}
              <a
                href={`tel:${WHATSAPP_NUMBER}`}
                className="hidden xl:flex items-center gap-2 text-white/40 hover:text-white/70 transition-colors duration-300 text-[12px] font-medium tracking-wide"
              >
                <i className="ri-phone-line text-[14px]" />
                {WHATSAPP_NUMBER}
              </a>

              {/* WhatsApp CTA */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-[#d4853a] text-white text-[13px] font-semibold rounded-lg transition-all duration-300 whitespace-nowrap hover:bg-[#c27630] hover:shadow-[0_8px_24px_rgba(212,133,58,0.35)] hover:-translate-y-[2px]"
              >
                <i className="ri-whatsapp-line text-[15px]" />
                WhatsApp
              </a>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2.5 rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors duration-300"
                aria-label="Toggle menu"
              >
                <div className="w-[22px] h-[18px] flex flex-col justify-between">
                  <span
                    className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 ${
                      mobileOpen ? 'rotate-45 translate-y-[8.25px]' : ''
                    }`}
                  />
                  <span
                    className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 ${
                      mobileOpen ? 'opacity-0 translate-x-2' : ''
                    }`}
                  />
                  <span
                    className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 ${
                      mobileOpen ? '-rotate-45 -translate-y-[8.25px]' : ''
                    }`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-[68px] md:top-[76px] left-0 right-0 z-40 overflow-hidden transition-all duration-400 ${
          mobileOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#1a1a1a] border-t border-white/[0.04] px-5 py-5 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
          <nav className="flex flex-col gap-0.5">
            {navLinks.map((link) => (
              <div key={link.path}>
                <a
                  href={link.path}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.path);
                  }}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-lg text-[14px] font-medium transition-colors duration-200 ${
                    location.pathname === link.path || (link.path === '/projects' && location.pathname.startsWith('/projects'))
                      ? 'text-white bg-white/[0.04]'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  {link.label}
                  {link.hasDropdown && (
                    <i className="ri-arrow-right-s-line text-white/30" />
                  )}
                </a>
                {link.hasDropdown && (
                  <div className="pl-4 mt-0.5">
                    {dropdownProjects.slice(0, 4).map((proj) => (
                      <a
                        key={proj.path}
                        href={proj.path}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(proj.path);
                        }}
                        className="flex flex-col px-4 py-2.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/[0.03] transition-colors duration-200"
                      >
                        <span className="text-[13px] font-medium">{proj.title}</span>
                        <span className="text-[11px] text-white/30">{proj.desc}</span>
                      </a>
                    ))}
                    <a
                      href="/projects"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick('/projects');
                      }}
                      className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold text-[#d4853a] hover:bg-[#d4853a]/5 rounded-lg transition-colors duration-200"
                    >
                      View All Projects
                      <i className="ri-arrow-right-line text-xs" />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="mt-5 pt-5 border-t border-white/[0.06]">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 bg-[#d4853a] text-white text-[14px] font-semibold rounded-lg hover:bg-[#c27630] transition-colors duration-300"
            >
              <i className="ri-whatsapp-line text-[18px]" />
              WhatsApp Consultation
            </a>
            <a
              href={`tel:${WHATSAPP_NUMBER}`}
              className="flex items-center justify-center gap-2 w-full mt-3 px-5 py-3 text-white/40 hover:text-white/70 text-[13px] font-medium transition-colors duration-300"
            >
              <i className="ri-phone-line" />
              {WHATSAPP_NUMBER}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}